import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/Buttons";
import { cn } from "@/lib/utils";

export interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /**
   * If set, the user must type this phrase exactly (case-sensitive) before
   * the Delete button enables. Use for irreversible / high-impact deletes
   * (project, file). Omit for low-impact deletes (matrix item) — the dialog
   * becomes a plain "Are you sure?" instead.
   */
  requiredPhrase?: string;
  confirmLabel?: string;
  onConfirm: () => Promise<void> | void;
  busy?: boolean;
}

export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  requiredPhrase,
  confirmLabel = "Delete",
  onConfirm,
  busy = false,
}: ConfirmDeleteDialogProps) {
  const [inputValue, setInputValue] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset input every time the dialog opens, and focus the phrase input.
  useEffect(() => {
    if (!open) return;
    setInputValue("");
    if (!requiredPhrase) return;
    // next tick so the input element is mounted
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, requiredPhrase]);

  // ESC key on the document while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const isConfirmed = !requiredPhrase || inputValue === requiredPhrase;
  const isLoading = isDeleting || busy;

  const handleConfirm = async () => {
    if (!isConfirmed || isLoading) return;
    setIsDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isConfirmed && !isLoading) {
      e.preventDefault();
      handleConfirm();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={() => !isLoading && onOpenChange(false)}
      role="presentation"
    >
      <div
        className="rounded-xl border p-6 max-w-sm w-full mx-4 shadow-lg"
        style={{
          backgroundColor: "#1F2933",
          borderColor: "rgba(229,231,235,0.08)",
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-delete-title"
      >
        <h2 id="confirm-delete-title" className="text-lg font-semibold text-white mb-2">
          {title}
        </h2>
        <p className="text-sm mb-6" style={{ color: "#9CA3AF" }}>
          {description}
        </p>

        {requiredPhrase && (
          <div className="mb-6">
            <label
              htmlFor="confirm-delete-input"
              className="block text-xs font-medium mb-2"
              style={{ color: "#D1D5DB" }}
            >
              Type{" "}
              <span className="font-mono bg-black/40 px-1.5 py-0.5 rounded text-white">
                {requiredPhrase}
              </span>{" "}
              to confirm
            </label>
            <input
              id="confirm-delete-input"
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={requiredPhrase}
              className={cn(
                "w-full px-3 py-2 rounded-lg border text-sm bg-white/5 text-white",
                "placeholder:text-gray-500 transition-colors focus:outline-none",
                inputValue === requiredPhrase
                  ? "border-green-500/50 focus:border-green-500"
                  : "border-white/10 focus:border-white/20",
              )}
              disabled={isLoading}
              autoComplete="off"
              spellCheck={false}
              aria-label="Confirmation phrase"
            />
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="md"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            className="flex-1"
            onClick={handleConfirm}
            disabled={!isConfirmed || isLoading}
            loading={isLoading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
